import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  MinusIcon, 
  CalendarIcon, 
  UserPlusIcon,
  EyeIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

import Helper from "../helpers";
import PopupModelWrapper from "./PopupModelWrapper";
import AddSession from "./AddSession";
import AddToken from "./AddToken";
import DeleteUserModal from "./DeleteUserModal";
import {fetchVenueUsers, changeUserTokens, softDeleteUserFromVenue} from "../Services/UserService.ts";
import SubscriptionStatusBadge from "./SubscriptionStatusBadge";

const UserAdminTable = () => {
    const [users, setUsers] = useState([]);
    const [currentVenueKey, setCurrentVenueKey] = useState("");
    const [venueKeyOptions, setVenueKeyOptions] = useState([]);
    const [selectedUser, setSelectedUser] = useState({});
    const [openAddSessionModal, setOpenAddSessionModal] = useState(false);
    const [openAddTokenModal, setOpenAddTokenModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterRole, setFilterRole] = useState("all");
    const [loading, setLoading] = useState(true);
    const [currentUserId, setCurrentUserId] = useState("");

    // Keep existing modal handlers unchanged
    const handleOpenAddSessionModal = () => setOpenAddSessionModal(true);
    const handleOpenAddTokenModal = (user) => {
        setOpenAddTokenModal(true);
        setSelectedUser(user);
    };
    const handleOpenDeleteModal = (user) => {
        setSelectedUser(user);
        setOpenDeleteModal(true);
    };
    const handleCloseAddSessionModal = () => setOpenAddSessionModal(false);
    const handleCloseAddTokenModal = (newTokenValue) => {
        selectedUser.tokens = newTokenValue;
        setOpenAddTokenModal(false);
        setSelectedUser({});
    };
    const handleCloseDeleteModal = () => {
        setOpenDeleteModal(false);
        setSelectedUser({});
    };

    // Quick token adjustment functions
    const adjustTokens = async (user, amount) => {
        try {
            // Create a mock API call for token adjustment
            // In real implementation, you'd have an endpoint for this
            const newTokenValue = (user.tokens || 0) + amount;
            await changeUserTokens(currentVenueKey, user.userId, amount);
            // Update local state immediately for better UX
            setUsers(users.map(u => 
                u.userId === user.userId 
                    ? { ...u, tokens: newTokenValue }
                    : u
            ));
            
            // You can add actual API call here when endpoint is available
            console.log(`Adjusted tokens for user ${user.userId} by ${amount}`);
        } catch (error) {
            console.error('Error adjusting tokens:', error);
        }
    };

    // Delete user from venue
    const handleDeleteUser = async () => {
        try {
            await softDeleteUserFromVenue(currentVenueKey, selectedUser.userId);
            
            // Remove user from local state immediately for better UX
            setUsers(users.filter(u => u.userId !== selectedUser.userId));
            
            alert('User successfully removed from venue');
        } catch (error) {
            console.error('Error removing user from venue:', error);
            alert('Failed to remove user from venue. Please try again.');
        }
    };

    // Keep existing useEffect unchanged
    useEffect(() => {
        const initialize = async () => {
            await Helper.getAdminVenues(setCurrentVenueKey, setVenueKeyOptions);
            
            // Get current user ID
            const userId = await Helper.getUserId();
            setCurrentUserId(userId);
            
            if (currentVenueKey) {
                let userData = await fetchVenueUsers(currentVenueKey);
                setUsers(userData);
            }
            setLoading(false);
        };

        initialize();
    }, [currentVenueKey]);

    // Filter users based on search and role
    const filteredUsers = users.filter(user => {
        const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesRole = filterRole === "all" || 
                          (filterRole === "admin" && user.isAdmin) ||
                          (filterRole === "member" && !user.isAdmin);
        
        return matchesSearch && matchesRole;
    });

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
            </div>
        );
    }

    if(venueKeyOptions.length === 0) {
        return (<>
        </>);
    }

    if (users.length === 0) {
        return (
            <div className="card-luxury p-8 text-center">
                <div className="mb-4">
                    <UserPlusIcon className="h-16 w-16 text-emerald-600/50 mx-auto" />
                </div>
                <h3 className="text-lg font-display font-medium text-accent-platinum mb-2">No users found</h3>
                <p className="text-emerald-600/70 font-body">No users are currently registered for this venue.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header and Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-xl sm:text-2xl font-display font-bold text-gradient-emerald">User Management</h2>
                    <p className="text-sm sm:text-base text-emerald-600/70 font-body">Manage venue members and their tokens</p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                    <div className="relative">
                        <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-emerald-600/60" />
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="input-field pl-10 w-full sm:min-w-[200px]"
                        />
                    </div>
                    
                    <select
                        value={filterRole}
                        onChange={(e) => setFilterRole(e.target.value)}
                        className="input-field w-full sm:min-w-[120px]"
                    >
                        <option value="all">All Users</option>
                        <option value="member">Members</option>
                        <option value="admin">Admins</option>
                    </select>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                <div className="card-luxury p-4 sm:p-6 hover-lift">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-xl flex items-center justify-center shadow-luxury">
                                <UserPlusIcon className="h-5 w-5 text-white" />
                            </div>
                        </div>
                        <div className="ml-3 sm:ml-4">
                            <p className="text-xs sm:text-sm font-body font-medium text-emerald-600/70">Total Users</p>
                            <p className="text-lg sm:text-2xl font-display font-semibold text-accent-platinum">{users.length}</p>
                        </div>
                    </div>
                </div>
                
                <div className="card-luxury p-4 sm:p-6 hover-lift">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-xl flex items-center justify-center shadow-luxury">
                                <EyeIcon className="h-5 w-5 text-white" />
                            </div>
                        </div>
                        <div className="ml-3 sm:ml-4">
                            <p className="text-xs sm:text-sm font-body font-medium text-emerald-600/70">Active Members</p>
                            <p className="text-lg sm:text-2xl font-display font-semibold text-accent-platinum">
                                {users.filter(u => !u.isAdmin).length}
                            </p>
                        </div>
                    </div>
                </div>
                
                <div className="card-luxury p-4 sm:p-6 hover-lift">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-xl flex items-center justify-center shadow-luxury">
                                <CalendarIcon className="h-5 w-5 text-white" />
                            </div>
                        </div>
                        <div className="ml-3 sm:ml-4">
                            <p className="text-xs sm:text-sm font-body font-medium text-emerald-600/70">Total Tokens</p>
                            <p className="text-lg sm:text-2xl font-display font-semibold text-accent-platinum">
                                {users.reduce((total, user) => total + (user.tokens || 0), 0)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Users Display - Desktop Table, Mobile Cards */}
            <div className="hidden lg:block">
                <div className="card-luxury overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-emerald-600/10">
                            <thead className="bg-luxury-charcoal/50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">User</th>
                                    <th className="px-6 py-3 text-left text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Role</th>
                                    <th className="px-6 py-3 text-left text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Subscription</th>
                                    <th className="px-6 py-3 text-center text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Tokens</th>
                                    <th className="px-6 py-3 text-center text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Sessions</th>
                                    <th className="px-6 py-3 text-center text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">Quick Actions</th>
                                    <th className="px-6 py-3 text-center text-xs font-display font-semibold text-emerald-300/80 uppercase tracking-wider">More Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-emerald-600/10">
                                {filteredUsers.map((user) => (
                                    <tr key={user.userId} className="hover:bg-emerald-600/5 transition-colors duration-200">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10">
                                                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center shadow-luxury">
                                                        <span className="text-sm font-display font-medium text-white">
                                                            {user.name?.charAt(0) || 'U'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-display font-medium text-accent-platinum">
                                                        {user.name}
                                                    </div>
                                                    <div className="text-sm text-emerald-600/60 font-body">
                                                        ID: {user.userId}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex px-2 py-1 text-xs font-display font-semibold rounded-full ${
                                                user.isAdmin 
                                                    ? 'bg-performance-pulse/20 text-performance-pulse border border-performance-pulse/30'
                                                    : 'bg-emerald-600/20 text-emerald-300 border border-emerald-600/30'
                                            }`}>
                                                {user.isAdmin ? 'Admin' : 'Member'}
                                            </span>
                                        </td>

                                        <td className="px-6 py-4">
                                            <SubscriptionStatusBadge user={user} />
                                        </td>

                                        <td className="px-6 py-4 text-center">
                                            <div className="flex items-center justify-center space-x-2">
                                                <span className="text-lg font-display font-semibold text-accent-platinum">
                                                    {user.tokens || 0}
                                                </span>
                                            </div>
                                        </td>
                                        
                                        <td className="px-6 py-4 text-center">
                                            <span className="text-sm font-body text-accent-platinum">
                                                {user.bookedSessions || 0}
                                            </span>
                                        </td>
                                        
                                        <td className="px-6 py-4">
                                            <div className="flex items-center justify-center space-x-1">
                                                <button
                                                    onClick={() => adjustTokens(user, -1)}
                                                    className="p-2 text-performance-pulse hover:bg-performance-pulse/10 rounded-lg transition-colors border border-transparent hover:border-performance-pulse/30"
                                                    title="Remove 1 token"
                                                >
                                                    <MinusIcon className="h-4 w-4" />
                                                </button>
                                                
                                                <button
                                                    onClick={() => adjustTokens(user, 1)}
                                                    className="p-2 text-emerald-400 hover:bg-emerald-600/10 rounded-lg transition-colors border border-transparent hover:border-emerald-600/30"
                                                    title="Add 1 token"
                                                >
                                                    <PlusIcon className="h-4 w-4" />
                                                </button>
                                                
                                                <button
                                                    onClick={() => handleOpenAddTokenModal(user)}
                                                    className="p-2 text-performance-cyan hover:bg-performance-cyan/10 rounded-lg transition-colors border border-transparent hover:border-performance-cyan/30"
                                                    title="Manage tokens"
                                                >
                                                    <PencilIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                        
                                        <td className="px-6 py-4">
                                            <div className="flex items-center justify-center space-x-1">
                                                <button
                                                    onClick={handleOpenAddSessionModal}
                                                    className="px-3 py-1 text-xs font-display font-semibold bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border border-emerald-600/30 hover:border-emerald-600/50 rounded-md transition-all"
                                                    title="Add session"
                                                >
                                                    Session
                                                </button>
                                                
                                                <button
                                                    className="px-3 py-1 text-xs font-display font-semibold bg-luxury-slate/50 hover:bg-luxury-slate/70 text-accent-platinum border border-accent-platinum/20 hover:border-accent-platinum/30 rounded-md transition-all"
                                                    title="View details"
                                                >
                                                    View
                                                </button>
                                                
                                                {user.userId !== currentUserId && (
                                                    <button
                                                        onClick={() => handleOpenDeleteModal(user)}
                                                        className="px-3 py-1 text-xs font-display font-semibold bg-performance-pulse/20 hover:bg-performance-pulse/30 text-performance-pulse border border-performance-pulse/30 hover:border-performance-pulse/50 rounded-md transition-all"
                                                        title="Remove user from venue"
                                                    >
                                                        <TrashIcon className="h-4 w-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    
                    {filteredUsers.length === 0 && (
                        <div className="text-center py-12">
                            <UserPlusIcon className="h-12 w-12 text-emerald-600/50 mx-auto mb-4" />
                            <p className="text-emerald-600/70 font-body">No users match your search criteria.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Mobile Card Layout */}
            <div className="lg:hidden space-y-4">
                {filteredUsers.map((user) => (
                    <div key={user.userId} className="card-luxury p-4 hover-lift">
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center">
                                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center mr-3 shadow-luxury">
                                    <span className="text-sm font-display font-medium text-white">
                                        {user.name?.charAt(0) || 'U'}
                                    </span>
                                </div>
                                <div>
                                    <div className="text-sm font-display font-medium text-accent-platinum">{user.name}</div>
                                    <div className="text-xs text-emerald-600/60 font-body">ID: {user.userId}</div>
                                    <div className="mt-1"><SubscriptionStatusBadge user={user} /></div>
                                </div>
                            </div>
                            <span className={`inline-flex px-2 py-1 text-xs font-display font-semibold rounded-full ${
                                user.isAdmin 
                                    ? 'bg-performance-pulse/20 text-performance-pulse border border-performance-pulse/30'
                                    : 'bg-emerald-600/20 text-emerald-300 border border-emerald-600/30'
                            }`}>
                                {user.isAdmin ? 'Admin' : 'Member'}
                            </span>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="text-center glass-luxury rounded-lg p-2 border border-emerald-600/20">
                                <div className="text-xs text-emerald-600/70 font-body">Tokens</div>
                                <div className="text-lg font-display font-semibold text-accent-platinum">{user.tokens || 0}</div>
                            </div>
                            <div className="text-center glass-luxury rounded-lg p-2 border border-emerald-600/20">
                                <div className="text-xs text-emerald-600/70 font-body">Sessions</div>
                                <div className="text-lg font-display font-semibold text-accent-platinum">{user.bookedSessions || 0}</div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center justify-center space-x-2">
                                <button
                                    onClick={() => adjustTokens(user, -1)}
                                    className="flex-1 py-2 px-3 text-sm font-display font-semibold bg-performance-pulse/20 hover:bg-performance-pulse/30 text-performance-pulse border border-performance-pulse/30 rounded-lg transition-all"
                                >
                                    <MinusIcon className="h-4 w-4 mx-auto" />
                                </button>
                                
                                <button
                                    onClick={() => handleOpenAddTokenModal(user)}
                                    className="flex-1 py-2 px-3 text-sm font-display font-semibold bg-performance-cyan/20 hover:bg-performance-cyan/30 text-performance-cyan border border-performance-cyan/30 rounded-lg transition-all"
                                >
                                    <PencilIcon className="h-4 w-4 mx-auto" />
                                </button>
                                
                                <button
                                    onClick={() => adjustTokens(user, 1)}
                                    className="flex-1 py-2 px-3 text-sm font-display font-semibold bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border border-emerald-600/30 rounded-lg transition-all"
                                >
                                    <PlusIcon className="h-4 w-4 mx-auto" />
                                </button>
                            </div>
                            
                            <div className="flex space-x-2">
                                <button
                                    onClick={handleOpenAddSessionModal}
                                    className="flex-1 btn-primary py-2 px-3 text-sm"
                                >
                                    Add Session
                                </button>
                                
                                <button
                                    className="flex-1 btn-outline py-2 px-3 text-sm"
                                >
                                    View Details
                                </button>
                            </div>
                            
                            {user.userId !== currentUserId && (
                                <button
                                    onClick={() => handleOpenDeleteModal(user)}
                                    className="w-full py-2 px-3 text-sm font-display font-semibold bg-performance-pulse/20 hover:bg-performance-pulse/30 text-performance-pulse border border-performance-pulse/30 hover:border-performance-pulse/50 rounded-lg transition-all flex items-center justify-center"
                                    title="Remove user from venue"
                                >
                                    <TrashIcon className="h-4 w-4 mr-2" />
                                    Remove from Venue
                                </button>
                            )}
                        </div>
                    </div>
                ))}
                
                {filteredUsers.length === 0 && (
                    <div className="card p-8 text-center">
                        <UserPlusIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-400">No users match your search criteria.</p>
                    </div>
                )}
            </div>

            {/* Keep existing modals unchanged */}
            <PopupModelWrapper 
                isOpen={openAddSessionModal} 
                handleClose={handleCloseAddSessionModal}
                ModelContent={AddSession} 
                Values={{venue : {key : currentVenueKey}}}
            />
            
            <PopupModelWrapper 
                isOpen={openAddTokenModal} 
                handleClose={handleCloseAddTokenModal}
                ModelContent={AddToken} 
                Values={{user : selectedUser, venue : {key : currentVenueKey}}}
            />
            
            <PopupModelWrapper 
                isOpen={openDeleteModal} 
                handleClose={handleCloseDeleteModal}
                ModelContent={DeleteUserModal} 
                Values={{user : selectedUser, onConfirm : handleDeleteUser}}
            />
        </div>
    );
};

export default UserAdminTable;